import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Input,
  Modal,
  Form,
  Select,
  Typography,
  Popconfirm,
  Upload,
  message,
  notification,
  Tooltip,
  Tag,
  Descriptions,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  UserSwitchOutlined,
  DownloadOutlined,
  KeyOutlined,
  TeamOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor,
  bulkCreateVendors,
  assignMerchant,
  getVendorLoginDetails,
} from "./services/vendorApi";
import { getMerchants } from "../Merchant/services/merchantApi";
import { useUserContext } from "../../../Contexts/user";
import ManageUsersModal from "./ManageUsersModal";
import "./styles.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Vendor = () => {
  const { userData } = useUserContext();
  const isMerchant = userData?.role === "merchant";
  const isAssistant = userData?.assistant === true;
  const isRestrictedToMerchant = isMerchant || isAssistant;
  const assistantMerchantEmail = userData?.merchant_email || "";

  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Create/Edit vendor modal
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm] = Form.useForm();
  const [vendorModalLoading, setVendorModalLoading] = useState(false);

  // Assign merchant modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningVendor, setAssigningVendor] = useState(null);
  const [assignForm] = Form.useForm();
  const [assignModalLoading, setAssignModalLoading] = useState(false);

  // Login details modal
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginDetails, setLoginDetails] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginVendorName, setLoginVendorName] = useState("");

  // CSV upload modal
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvUploading, setCsvUploading] = useState(false);

  // Export login details
  const [exportingLogins, setExportingLogins] = useState(false);

  // Manage users modal
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersVendor, setUsersVendor] = useState(null);

  // ---- Data fetching ----

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVendors();
      const keyed = data.map((v, i) => ({ ...v, key: v.id || i }));
      setVendors(keyed);
      setFilteredVendors(keyed);
    } catch {
      message.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMerchants = useCallback(async () => {
    try {
      const data = await getMerchants();
      setMerchants(data);
    } catch {
      // Silently fail — merchants are only needed for the assign dropdown
    }
  }, []);

  useEffect(() => {
    fetchVendors();
    fetchMerchants();
  }, [fetchVendors, fetchMerchants]);

  // ---- Search ----

  useEffect(() => {
    if (!searchText) {
      setFilteredVendors(vendors);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredVendors(
      vendors.filter(
        (v) =>
          v.vendorCode?.toLowerCase().includes(lower) ||
          v.vendorName?.toLowerCase().includes(lower) ||
          v.merchantName?.toLowerCase().includes(lower)
      )
    );
  }, [searchText, vendors]);

  // ---- Create / Edit vendor ----

  const openCreateModal = () => {
    setEditingVendor(null);
    vendorForm.resetFields();
    if (isRestrictedToMerchant && merchants.length > 0) {
      const merchantId = isAssistant
        ? merchants.find((m) => m.email === assistantMerchantEmail)?.id
        : merchants[0].id;
      if (merchantId) vendorForm.setFieldsValue({ merchantId });
    }
    setVendorModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingVendor(record);
    vendorForm.setFieldsValue({
      vendorCode: record.vendorCode,
      vendorName: record.vendorName,
      firstName: record.firstName || undefined,
      lastName: record.lastName || undefined,
      emailAddress: record.emailAddress || undefined,
      regularTerms: record.regularTerms || undefined,
      moq: record.moq || undefined,
      freightTerms: record.freightTerms || undefined,
      merchantId: record.merchantId || undefined,
    });
    setVendorModalOpen(true);
  };

  const handleVendorSubmit = async () => {
    try {
      const values = await vendorForm.validateFields();
      setVendorModalLoading(true);
      if (editingVendor) {
        await updateVendor(editingVendor.id, values);
        message.success("Vendor updated successfully");
      } else {
        await createVendor(values);
        message.success("Vendor created successfully");
      }
      setVendorModalOpen(false);
      vendorForm.resetFields();
      fetchVendors();
    } catch (err) {
      if (err.errorFields) return;
      message.error("Failed to save vendor");
    } finally {
      setVendorModalLoading(false);
    }
  };

  // ---- Delete vendor ----

  const handleDeleteVendor = async (record) => {
    try {
      await deleteVendor(record.id);
      message.success("Vendor deleted successfully");
      fetchVendors();
    } catch {
      message.error("Failed to delete vendor");
    }
  };

  // ---- Assign merchant ----

  const openAssignModal = (record) => {
    setAssigningVendor(record);
    assignForm.setFieldsValue({
      merchantId: record.merchantId || undefined,
    });
    setAssignModalOpen(true);
  };

  const handleAssignSubmit = async () => {
    try {
      const values = await assignForm.validateFields();
      setAssignModalLoading(true);
      await assignMerchant(assigningVendor.id, values.merchantId);
      message.success("Merchant assigned successfully");
      setAssignModalOpen(false);
      assignForm.resetFields();
      fetchVendors();
    } catch (err) {
      if (err.errorFields) return;
      message.error("Failed to assign merchant");
    } finally {
      setAssignModalLoading(false);
    }
  };

  // ---- Login details ----

  const showVendorLogin = async (record) => {
    setLoginVendorName(record.vendorName);
    setLoginDetails(null);
    setLoginModalOpen(true);
    setLoginLoading(true);
    try {
      const data = await getVendorLoginDetails(record.id);
      setLoginDetails(data);
    } catch {
      message.error("Failed to load login details");
      setLoginModalOpen(false);
    } finally {
      setLoginLoading(false);
    }
  };

  // ---- Export all login details ----

  const handleExportLoginDetails = async () => {
    setExportingLogins(true);
    try {
      // Headers match the upload template exactly — password appended as extra column
      const rows = [[
        "vendor_code", "vendor_name", "first_name", "last_name",
        "email_address", "regular_terms", "moq", "freight_terms",
        "merchant_email", "vendor_password",
      ]];
      const results = await Promise.allSettled(
        vendors.map((v) => getVendorLoginDetails(v.id).then((d) => ({ vendor: v, details: d })))
      );
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const { vendor, details } = r.value;
          // Resolve merchant email from the already-loaded merchants list
          const merchantEmail = merchants.find((m) => m.id === vendor.merchantId)?.email || "";
          rows.push([
            vendor.vendorCode    || "",
            vendor.vendorName    || "",
            vendor.firstName     || "",
            vendor.lastName      || "",
            vendor.emailAddress  || "",
            vendor.regularTerms  || "",
            vendor.moq           || "",
            vendor.freightTerms  || "",
            merchantEmail,
            details.password     || "",
          ]);
        }
      });
      const csvContent = rows
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vendor-login-details.csv";
      a.click();
      URL.revokeObjectURL(url);
      message.success("Login details exported successfully");
    } catch {
      message.error("Failed to export login details");
    } finally {
      setExportingLogins(false);
    }
  };

  // ---- CSV upload ----

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];

    // Find column indices from header
    const headerLine = lines[0].replace(/^\uFEFF/, "");
    const headers = parseCSVLine(headerLine).map((h) => h.trim().toLowerCase());
    const vendorCodeIdx = headers.findIndex((h) => h === "vendor code" || h === "vendor_code");
    const vendorNameIdx = headers.findIndex((h) => h === "vendor name" || h === "vendor_name");
    const merchantEmailIdx = headers.findIndex((h) => h === "merchant_email" || h === "merchant email");
    const firstNameIdx = headers.findIndex((h) => h === "first_name" || h === "first name");
    const lastNameIdx = headers.findIndex((h) => h === "last_name" || h === "last name");
    const emailAddressIdx = headers.findIndex((h) => h === "email_address" || h === "email address" || h === "email");
    const regularTermsIdx = headers.findIndex((h) => h === "regular_terms" || h === "regular terms");
    const moqIdx = headers.findIndex((h) => h === "moq");
    const freightTermsIdx = headers.findIndex((h) => h === "freight_terms" || h === "freight terms");

    // Detect vendor_user1, vendor_user2, ... columns (any number)
    const vendorUserIndices = headers.reduce((acc, h, idx) => {
      if (/^vendor_user\d+$/.test(h)) acc.push(idx);
      return acc;
    }, []);

    // Only vendor_code column is required to be present
    if (vendorCodeIdx === -1) {
      message.error("CSV is missing required column: vendor_code");
      return [];
    }

    const results = [];
    let skipped = 0;
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const vendorCode = values[vendorCodeIdx]?.trim();
      if (!vendorCode) continue; // blank row

      const vendorName = vendorNameIdx !== -1 ? values[vendorNameIdx]?.trim() : "";
      const firstName = firstNameIdx !== -1 ? values[firstNameIdx]?.trim() : "";
      const lastName = lastNameIdx !== -1 ? values[lastNameIdx]?.trim() : "";
      const emailAddress = emailAddressIdx !== -1 ? values[emailAddressIdx]?.trim() : "";
      const regularTerms = regularTermsIdx !== -1 ? values[regularTermsIdx]?.trim() : "";
      const moq = moqIdx !== -1 ? values[moqIdx]?.trim() : "";
      const freightTerms = freightTermsIdx !== -1 ? values[freightTermsIdx]?.trim() : "";
      const merchantEmail = merchantEmailIdx !== -1 ? values[merchantEmailIdx]?.trim() : "";

      // Collect non-empty vendor user emails
      const vendorUsers = vendorUserIndices
        .map((idx) => values[idx]?.trim())
        .filter(Boolean);

      // Build object with only non-empty fields so backend can distinguish
      // between "field not provided" and "field explicitly set"
      const vendor = { vendorCode };
      if (vendorName) vendor.vendorName = vendorName;
      if (firstName) vendor.firstName = firstName;
      if (lastName) vendor.lastName = lastName;
      if (emailAddress) vendor.emailAddress = emailAddress;
      if (regularTerms) vendor.regularTerms = regularTerms;
      if (moq) vendor.moq = moq;
      if (freightTerms) vendor.freightTerms = freightTerms;
      if (vendorUsers.length > 0) vendor.vendorUsers = vendorUsers;
      if (!isRestrictedToMerchant && merchantEmail) vendor.merchantEmail = merchantEmail;

      results.push(vendor);
    }

    if (skipped > 0) {
      message.warning(`${skipped} row(s) skipped (missing vendor_code).`);
    }

    return results;
  };

  const handleCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) return;
      setCsvData(parsed);
      setCsvModalOpen(true);
    };
    reader.readAsText(file);
    return false;
  };

  const handleBulkUpload = async () => {
    setCsvUploading(true);
    try {
      const effectiveMerchantEmail = isAssistant ? assistantMerchantEmail : userData.email;
      const vendorsToUpload = isRestrictedToMerchant
        ? csvData.map((v) => ({ ...v, merchantEmail: effectiveMerchantEmail }))
        : csvData;
      const res = await bulkCreateVendors(vendorsToUpload);
      const created = res?.created ?? 0;
      const updated = res?.updated ?? 0;
      const failed = res?.failed ?? [];

      if (created > 0 || updated > 0) {
        const parts = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        message.success(`Vendors uploaded: ${parts.join(", ")}.`);
      }

      failed.forEach(({ vendorCode, error }) => {
        notification.error({
          message: `Failed: ${vendorCode}`,
          description: error,
          duration: 0,
        });
      });

      setCsvModalOpen(false);
      setCsvData([]);
      fetchVendors();
    } catch {
      message.error("Failed to upload vendors");
    } finally {
      setCsvUploading(false);
    }
  };

  // ---- Manage users ----
  // All state/handlers for this modal live in ManageUsersModal itself so that
  // typing in its email field doesn't re-render this (large) page.

  const openUsersModal = (record) => {
    setUsersVendor(record);
    setUsersModalOpen(true);
  };

  // ---- Table columns ----

  const columns = [
    {
      title: "Vendor Code",
      dataIndex: "vendorCode",
      sorter: (a, b) => (a.vendorCode || "").localeCompare(b.vendorCode || ""),
      width: 140,
    },
    {
      title: "Vendor Name",
      dataIndex: "vendorName",
      sorter: (a, b) => (a.vendorName || "").localeCompare(b.vendorName || ""),
    },
    {
      title: "Merchant",
      dataIndex: "merchantName",
      sorter: (a, b) => (a.merchantName || "").localeCompare(b.merchantName || ""),
      render: (text) =>
        text ? (
          <Tag color="green">{text}</Tag>
        ) : (
          <Text type="secondary">Unassigned</Text>
        ),
    },
    {
      title: "Actions",
      width: 380,
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="View Login Details">
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => showVendorLogin(record)}
            >
              Login
            </Button>
          </Tooltip>
          <Tooltip title="Manage Users">
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => openUsersModal(record)}
            >
              Users
            </Button>
          </Tooltip>
          <Tooltip title="Assign Merchant">
            <Button
              size="small"
              icon={<UserSwitchOutlined />}
              onClick={() => openAssignModal(record)}
            >
              Assign
            </Button>
          </Tooltip>
          <Tooltip title="Edit Vendor">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this vendor?"
            description="This will permanently remove this vendor."
            onConfirm={() => handleDeleteVendor(record)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete Vendor">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ---- CSV preview columns ----

  const csvColumns = [
    { title: "Vendor Code", dataIndex: "vendorCode", width: 120 },
    {
      title: (
        <span>
          Perceived Action{" "}
          <Tooltip title="The system identified this based on the given details — whether you want to create a new vendor or update an existing one.">
            <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
          </Tooltip>
        </span>
      ),
      width: 150,
      render: (_, row) => {
        const isNew = row.vendorName && row.firstName && row.lastName && row.emailAddress;
        return (
          <span style={{ color: isNew ? "#52c41a" : "#faad14", fontWeight: 600, fontSize: 12 }}>
            {isNew ? "Create" : "Update"}
          </span>
        );
      },
    },
    { title: "Vendor Name", dataIndex: "vendorName", width: 160 },
    { title: "First Name", dataIndex: "firstName", render: (t) => t || "-", width: 110 },
    { title: "Last Name", dataIndex: "lastName", render: (t) => t || "-", width: 110 },
    { title: "Email Address", dataIndex: "emailAddress", render: (t) => t || "-", width: 180 },
    { title: "Regular Terms", dataIndex: "regularTerms", render: (t) => t || "-", width: 130 },
    { title: "MOQ", dataIndex: "moq", render: (t) => t || "-", width: 80 },
    { title: "Freight Terms", dataIndex: "freightTerms", render: (t) => t || "-", width: 130 },
    {
      title: "Merchant Email",
      dataIndex: "merchantEmail",
      render: (text) => text || "-",
      width: 180,
    },
    {
      title: "Vendor Users",
      dataIndex: "vendorUsers",
      width: 220,
      render: (users) =>
        users?.length > 0 ? (
          <Space direction="vertical" size={2}>
            {users.map((email) => (
              <Text key={email} style={{ fontSize: 12 }}>{email}</Text>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div className="vendor-container">
      <Card className="vendor-card">
        {/* Header */}
        <Row
          justify="space-between"
          align="middle"
          className="vendor-header"
          gutter={[16, 16]}
          wrap
        >
          <Col>
            <Title level={4} className="vendor-title">
              Vendor Management
            </Title>
          </Col>
          <Col>
            <Space wrap>
              <Button
                icon={<DownloadOutlined />}
                loading={exportingLogins}
                onClick={handleExportLoginDetails}
              >
                Export Logins
              </Button>
              <Upload
                accept=".csv"
                showUploadList={false}
                beforeUpload={handleCSVFile}
              >
                <Button icon={<UploadOutlined />}>Upload CSV</Button>
              </Upload>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Add Vendor
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Search */}
        <Row
          justify="space-between"
          align="middle"
          className="table-controls"
          gutter={[16, 16]}
        >
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search vendors..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="vendor-search"
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: "right" }}>
            <Text type="secondary">
              {filteredVendors.length} vendor{filteredVendors.length !== 1 ? "s" : ""}
            </Text>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredVendors}
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} vendors`,
          }}
          className="vendor-table"
        />
      </Card>

      {/* ========== CREATE / EDIT VENDOR MODAL ========== */}
      <Modal
        title={editingVendor ? "Edit Vendor" : "Add New Vendor"}
        open={vendorModalOpen}
        onCancel={() => {
          setVendorModalOpen(false);
          vendorForm.resetFields();
        }}
        onOk={handleVendorSubmit}
        confirmLoading={vendorModalLoading}
        okText={editingVendor ? "Update" : "Create"}
        width={600}
        destroyOnClose
      >
        <Form form={vendorForm} layout="vertical" className="vendor-form">
          <Form.Item
            name="vendorCode"
            label="Vendor Code"
            rules={[{ required: true, message: "Please enter vendor code" }]}
          >
            <Input placeholder="e.g. S97" disabled={!!editingVendor} />
          </Form.Item>
          <Form.Item
            name="vendorName"
            label="Vendor Name"
            rules={[{ required: true, message: "Please enter vendor name" }]}
          >
            <Input placeholder="Enter vendor name" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="emailAddress"
            label="Email Address"
            rules={[{ required: true, message: "Please enter email address" }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="regularTerms"
                label="Regular Terms"
                rules={[{ required: true, message: "Please enter regular terms" }]}
              >
                <Input placeholder="e.g. Net 30" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="moq"
                label="MOQ"
                rules={[{ required: true, message: "Please enter MOQ" }]}
              >
                <Input placeholder="Minimum order quantity" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="freightTerms"
            label="Freight Terms"
            rules={[{ required: true, message: "Please enter freight terms" }]}
          >
            <Input placeholder="e.g. FOB, Prepaid" />
          </Form.Item>
          <Form.Item
            name="merchantId"
            label="Assign Merchant"
            rules={[{ required: true, message: "Please assign a merchant" }]}
          >
            <Select
              placeholder="Select a merchant"
              allowClear={!isRestrictedToMerchant}
              showSearch
              optionFilterProp="children"
              disabled={isRestrictedToMerchant}
            >
              {merchants.map((m) => (
                <Option key={m.id} value={m.id}>
                  {m.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ========== ASSIGN MERCHANT MODAL ========== */}
      <Modal
        title={`Assign Merchant to ${assigningVendor?.vendorName || ""}`}
        open={assignModalOpen}
        onCancel={() => {
          setAssignModalOpen(false);
          assignForm.resetFields();
        }}
        onOk={handleAssignSubmit}
        confirmLoading={assignModalLoading}
        okText="Assign"
        destroyOnClose
      >
        <Form form={assignForm} layout="vertical" className="vendor-form">
          <Form.Item
            name="merchantId"
            label="Merchant"
            rules={[{ required: true, message: "Please select a merchant" }]}
          >
            <Select
              placeholder="Select a merchant"
              showSearch
              optionFilterProp="children"
            >
              {merchants.map((m) => (
                <Option key={m.id} value={m.id}>
                  {m.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {assigningVendor?.merchantName && (
            <Text type="secondary">
              Currently assigned to: <strong>{assigningVendor.merchantName}</strong>
            </Text>
          )}
        </Form>
      </Modal>

      {/* ========== CSV PREVIEW MODAL ========== */}
      <Modal
        title="CSV Upload Preview"
        open={csvModalOpen}
        onCancel={() => {
          setCsvModalOpen(false);
          setCsvData([]);
        }}
        width={700}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setCsvModalOpen(false);
              setCsvData([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<DownloadOutlined />}
            loading={csvUploading}
            onClick={handleBulkUpload}
          >
            Upload {csvData.length} Vendors
          </Button>,
        ]}
        destroyOnClose
      >
        <div className="csv-preview-header">
          <Text>
            Found <strong>{csvData.length}</strong> vendors in the CSV file.
            Review and confirm to upload.
          </Text>
        </div>
        <Table
          columns={csvColumns}
          dataSource={csvData.map((d, i) => ({ ...d, key: i }))}
          size="small"
          scroll={{ x: "max-content", y: 400 }}
          pagination={false}
          className="csv-preview-table"
        />
      </Modal>

      {/* ========== MANAGE USERS MODAL ========== */}
      <ManageUsersModal
        open={usersModalOpen}
        vendor={usersVendor}
        onClose={() => setUsersModalOpen(false)}
      />

      {/* ========== LOGIN DETAILS MODAL ========== */}
      <Modal
        title={`${loginVendorName} — Login Details`}
        open={loginModalOpen}
        onCancel={() => setLoginModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setLoginModalOpen(false)}>
            Close
          </Button>,
        ]}
        destroyOnClose
      >
        {loginLoading ? (
          <div className="login-loading">
            <Spin />
          </div>
        ) : loginDetails ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Email">
              {loginDetails.email}
            </Descriptions.Item>
            <Descriptions.Item label="Password">
              {loginDetails.password}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">No login details available.</Text>
        )}
      </Modal>
    </div>
  );
};

export default Vendor;
